import { ServicePlayer, type PlayerDevice } from "./service_player";
import { AsyncEvent, sleep } from "../util";
import spotify, { SpotifyClient } from "../services/spotify";
import type { CurrentTrackItem, PlaybackState, RepeatMode } from "./types";

const SpotifyRepeatMode: RepeatMode[] = ["off", "track", "context"];

export class SpotifyPlayer extends ServicePlayer {
  service = "spotify" as const;
  supportsDevices = true;

  private client: SpotifyClient;
  player!: Spotify.Player;
  deviceId = "";
  currentDeviceId = "";
  private playerLoadedEvent: AsyncEvent | null = null;
  private spotifyPlayerIsReady = false;
  private transferNonce = 0;

  constructor(client?: SpotifyClient) {
    super();
    this.client = client || spotify;
  }

  // spotify player create is done later, it must be deferred until user interaction to avoid browser autoplay restrictions
  async init() {
    this.progressTimer();
    console.debug("CMS init spotify player");
    await this.createSpotifyPlayer();
  }

  private async progressTimer() {
    // Keep incrementing progressMs of the playback state
    let lastUpdate = +new Date();
    while (true) {
      await sleep(1000);
      const now = +new Date();
      const elapsed = now - lastUpdate;
      lastUpdate = now;
      if (this.playbackState.isPaused || !this.playbackState.currentTrack) {
        continue;
      }
      this.playbackState.progressMs = Math.min(
        this.playbackState.progressMs + elapsed,
        this.playbackState.currentTrack.durationMs
      );
      this.dispatchState();
    }
  }

  private stateFromSpotifyState(state: Spotify.PlaybackState): PlaybackState {
    const rawTrack = state.track_window?.current_track;
    return {
      ...this.playbackState,
      progressMs: state.position,
      isPaused: state.paused,
      // set repeat to off/context/track
      repeatMode: SpotifyRepeatMode[state.repeat_mode],
      isShuffle: state.shuffle,
      isLoading: state.loading,
      originalEvent: state,
      currentTrack:
        rawTrack && rawTrack.id
          ? {
              id: rawTrack.id,
              name: rawTrack.name,
              artists: rawTrack.artists.map((artist) => ({
                name: artist.name,
              })),
              album: {
                name: rawTrack.album.name,
              },
              durationMs: rawTrack.duration_ms,
              image: rawTrack.album.images[0]?.url,
            }
          : null,
    };
  }

  async activateSpotifyPlayer(): Promise<void> {
    if (this.player) {
      await this.player.activateElement();
    }
  }

  private async createSpotifyPlayer() {
    // Create the spotify SDK player context.
    // This method needs to be called before the first playback (e.g. in playPlaylist/playAlbum)
    // but has to be in response to a click event or similar to get around browser autoplay restrictions.
    // It's safe to call this multiple times and concurrently, only one player instance will be created

    // Player is already created or in the progress of being created
    if (this.spotifyPlayerIsReady) {
      return;
    } else if (this.playerLoadedEvent !== null) {
      return await this.playerLoadedEvent.wait();
    }

    this.playbackState.isLoading = true;
    this.dispatchState();
    // Otherwise create a new player
    this.playerLoadedEvent = new AsyncEvent();
    // console.log("Create spotify player");
    this.player = this.client.createPlayer({
      name: "UMG",
      volume: 1.0,
    });
    // @ts-ignore
    window.spotifyPlayer = this.player;
    this.player.activateElement();

    this.player.addListener("player_state_changed", (state) => {
      if (state === null || !state.playback_id) {
        // null state can happen when switching devices, ignore this
        // next state should be correct
        return;
      }
      this.playbackState = this.stateFromSpotifyState(state);
      this.dispatchState();
    });

    this.player.addListener("ready", (device) => {
      console.debug("Spotify player ready", device);
      this.deviceId = device.device_id;
      if (!this.currentDeviceId) {
        this.currentDeviceId = this.deviceId;
      }
      this.spotifyPlayerIsReady = true;
      if (this.playerLoadedEvent) {
        this.playerLoadedEvent.trigger();
      }
    });
    this.player.addListener("not_ready", () => {
      if (this.playerLoadedEvent) {
        this.playerLoadedEvent.trigger();
      }
    });
    this.player.addListener("initialization_error", (event: any) => {
      if (this.playerLoadedEvent) {
        this.playerLoadedEvent.trigger();
      }
      document.dispatchEvent(
        new CustomEvent("umg:player:initializationError", {
          detail: event,
        })
      );
    });

    this.player.addListener("authentication_error", () => {
      if (this.playerLoadedEvent) {
        this.playerLoadedEvent.trigger();
      }
    });

    this.player.addListener("account_error", () => {
      if (this.playerLoadedEvent) {
        this.playerLoadedEvent.trigger();
      }
    });

    this.player.addListener("playback_error", () => {
      if (this.playerLoadedEvent) {
        this.playerLoadedEvent.trigger();
      }
    });

    await this.player.connect();
    await this.playerLoadedEvent.wait();
    this.playbackState.isLoading = false;
    this.dispatchState();
  }

  public async pause() {
    await this.player.pause();
  }

  public async resume() {
    await this.player.resume();
  }

  public async seek(positionMs: number) {
    await this.player.seek(positionMs);
  }

  public async togglePlayback(shuffle?: boolean) {
    await this.client.togglePlayback(shuffle || false);
  }

  public async setRepeatMode(mode: RepeatMode) {
    await this.client.setRepeatMode(mode);
  }

  public async previousTrack() {
    await this.player.previousTrack();
  }

  public async nextTrack() {
    await this.player.nextTrack();
  }

  public async getDevices(): Promise<PlayerDevice[]> {
    const data = await this.client.getDevices();
    const devices = data.devices
      .filter((device) => !!device.id && !device.is_restricted)
      .map(
        (device) =>
          <PlayerDevice>{
            id: device.id as string,
            active: device.is_active,
            name: device.name,
            type: device.type,
            volume:
              device.volume_percent === null ? 1 : device.volume_percent / 100,
            // @ts-ignore
            supportsVolume: device.supports_volume,
          }
      );
    // Mark the current player device as active if no active device is listed
    let activeDevice = devices.find((device) => device.active);
    if (!activeDevice) {
      activeDevice =
        devices.find((device) => device.id === this.currentDeviceId) ||
        devices[0] ||
        null;
    }
    if (activeDevice) {
      activeDevice.active = true;
    }
    for (const device of devices) {
      if (device.active) {
        this.currentDeviceId = device.id;
        this.playbackState.volume = device.volume;
        this.playbackState.supportsVolume = device.supportsVolume;
      }
    }
    // Dispatch update
    this.dispatchState();
    return devices;
  }

  public async setDevice(deviceId: string): Promise<void> {
    // Force the state back to playing after transfer as it switches to paused and we don't get state events from other devices.
    let count = 0;
    const nonce = this.transferNonce;
    this.transferNonce += 1;
    const listener = async (event: CustomEvent<PlaybackState>) => {
      // console.log("POST TRANSFER PLAYBACK STATE", event.detail.isPaused);
      count += 1;
      if (this.transferNonce === nonce && event.detail.isPaused) {
        this.playbackState.isPaused = false;
        this.currentDeviceId = deviceId;
        this.dispatchState();
      }
      if (count === 2) {
        document.removeEventListener(
          "umg:player:playbackStateChanged",
          listener
        );
      }
    };
    document.addEventListener("umg:player:playbackStateChanged", listener);
    // TODO: Should set `play` param based on current state
    await this.client.transferPlayback(deviceId);
    this.currentDeviceId = deviceId;
  }

  public async playPlaylist(playlistId: string, options = {}): Promise<void> {
    await this.activateSpotifyPlayer();
    await this.client.playPlaylist(playlistId, {
      ...options,
      deviceId: this.currentDeviceId,
    });
    await this.resume();
  }

  public async playAlbum(albumId: string, options = {}): Promise<void> {
    await this.activateSpotifyPlayer();
    await this.client.playAlbum(albumId, {
      ...options,
      deviceId: this.currentDeviceId,
    });
    await this.resume();
  }

  public async playTrack(trackId: string, options = {}): Promise<void> {
    await this.activateSpotifyPlayer();
    await this.client.playTrack(trackId, {
      ...options,
      deviceId: this.currentDeviceId,
    });
    await this.resume();
  }

  async playTracks(trackIds: string[], options = {}): Promise<void> {
    await this.activateSpotifyPlayer();
    await this.client.playTracks(trackIds, {
      ...options,
      deviceId: this.currentDeviceId,
    });
    await this.resume();
  }

  async enqueueTracks(trackIds: string[]): Promise<void> {
    // Spotify API has very poor queue management, so this is not supported for now
  }

  async setVolume(volume: number): Promise<void> {
    await this.client.setVolume(volume);
    this.playbackState.volume = volume;
    this.dispatchState();
  }
}

class SpotifyQueue {
  private queueTracks: Spotify.Track[];
  private currentTrackIndex = 0;
  client: SpotifyClient;

  constructor(client: SpotifyClient) {
    this.client = client;
    this.queueTracks = [];
  }
}
